'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useRouter } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { BedDouble, X, Loader2, Check } from 'lucide-react';
import { getOwnHold, clearOwnHold } from '@/lib/own-hold';
import { formatShortDate } from '@/lib/calendar-utils';

interface HoldRoom {
  roomSlug: string;
  roomName: string;
}

interface HoldData {
  invoiceId: string;
  checkIn: string;
  checkOut: string;
  rooms: HoldRoom[];
}

type Status = 'idle' | 'releasing' | 'released';

/**
 * Global reclaim affordance for a guest's own unpaid PYN hold (2026-07 lockout).
 *
 * Mounted once in the public layout, it renders NOTHING on the server and on the
 * first client paint — so it has zero effect on the SSR/SEO output. Only after
 * hydration does it read the durable own-hold token ([[own-hold]]) and, if a
 * live hold exists, confirm it against `retry-data` (which serves only
 * non-completed bookings). When confirmed it shows a small banner letting the
 * guest either resume their booking (`/checkout?retry=…`, the same path the
 * recovery email uses) or release the hold outright (`/api/checkout/release`),
 * freeing the room immediately instead of waiting for the abandoned-cart sweep.
 *
 * If the token points at a booking that is no longer a live hold (paid, expired,
 * already swept), retry-data returns non-200 and we clear the stale token and
 * render nothing — self-cleaning.
 */
export function OwnHoldBanner() {
  const t = useTranslations('ownHold');
  const router = useRouter();
  const pathname = usePathname();
  const [hold, setHold] = useState<HoldData | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [dismissed, setDismissed] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Don't compete with the active checkout flow (which manages its own hold) or
  // the post-payment result screens (all under /checkout/*).
  const onCheckout = pathname?.includes('/checkout') ?? false;

  useEffect(() => {
    if (onCheckout) return;
    const invoiceId = getOwnHold();
    if (!invoiceId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/checkout/retry-data?invoice_id=${encodeURIComponent(invoiceId)}`,
          { cache: 'no-store' },
        );
        if (!res.ok) {
          // Booking no longer a live, resumable hold — forget the stale token.
          clearOwnHold();
          return;
        }
        const d = await res.json();
        const rooms: HoldRoom[] = Array.isArray(d?.rooms)
          ? d.rooms.map((r: { roomSlug: string; roomName?: string }) => ({
              roomSlug: r.roomSlug,
              roomName: r.roomName || '',
            }))
          : [];
        if (cancelled || rooms.length === 0 || !d?.checkIn || !d?.checkOut) return;
        setHold({ invoiceId, checkIn: d.checkIn, checkOut: d.checkOut, rooms });
      } catch {
        /* transient — leave the token, try again next navigation */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onCheckout]);

  // Report the card's live height as a CSS var so other fixed bottom elements
  // (the cookie-consent banner) can stack above it instead of overlapping — the
  // same idiom the selection bar uses with `--selection-bar-height`. When the
  // card isn't rendered (no hold / dismissed / on checkout / released-then-gone)
  // the var resets to 0 so those elements drop back to their normal position.
  useEffect(() => {
    const root = document.documentElement;
    const el = cardRef.current;
    if (!el) {
      root.style.setProperty('--own-hold-banner-height', '0px');
      return;
    }
    // +12px keeps a small gap between this card and whatever stacks above it.
    const update = () =>
      root.style.setProperty('--own-hold-banner-height', `${el.offsetHeight + 12}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty('--own-hold-banner-height', '0px');
    };
  }, [hold, dismissed, onCheckout, status]);

  const handleResume = useCallback(() => {
    if (!hold) return;
    router.push(`/checkout?retry=${encodeURIComponent(hold.invoiceId)}`);
  }, [hold, router]);

  const handleRelease = useCallback(async () => {
    if (!hold || status === 'releasing') return;
    setStatus('releasing');
    try {
      const res = await fetch('/api/checkout/release', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_id: hold.invoiceId }),
      });
      // 200 (released or already-released) and 404/409 (nothing to release) all
      // mean "this token no longer holds a room" — clear it either way.
      clearOwnHold();
      if (res.ok) {
        setStatus('released');
        setTimeout(() => setHold(null), 4000);
      } else {
        setHold(null);
      }
    } catch {
      // Network failure — let the guest try again.
      setStatus('idle');
    }
  }, [hold, status]);

  if (!hold || dismissed || onCheckout) return null;

  // Singular vs plural is branched here (not via inline ICU) because these are
  // full sentences with contractions — a poor fit for inline plural syntax. Room
  // counts are always small, so the plural copy is grammatically correct for
  // every real case (RO's `few` form covers 2–19).
  const multi = hold.rooms.length > 1;
  const count = hold.rooms.length;
  const roomName = hold.rooms[0]?.roomName || t('yourRoom');
  const dateLabel = `${formatShortDate(hold.checkIn)} – ${formatShortDate(hold.checkOut)}`;

  return (
    // Bottom-anchored and offset above the selection bar (via its exposed CSS
    // var) so it never overlaps that bar or the fixed top header. Below the
    // mobile drawer (z-60). Renders nothing until hydration confirms a live hold.
    <div
      className="fixed inset-x-0 z-[54] flex justify-center px-3 pointer-events-none"
      style={{ bottom: 'calc(var(--selection-bar-height, 0px) + 1rem + env(safe-area-inset-bottom, 0px))' }}
    >
      <div ref={cardRef} className="pointer-events-auto w-full max-w-2xl rounded-xl border border-amber-300 bg-amber-50 shadow-lg shadow-amber-900/10 px-4 py-3 sm:px-5 sm:py-4">
        {status === 'released' ? (
          <div className="flex items-center gap-2 text-sm text-amber-900">
            <Check className="h-4 w-4 shrink-0" />
            <span>{multi ? t('releasedPlural') : t('released')}</span>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Header: what's going on + why the room looks unavailable */}
            <div className="flex items-start gap-3">
              <BedDouble className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold">{multi ? t('titlePlural') : t('title')}</p>
                <p className="text-amber-800/90">
                  {multi
                    ? t('descriptionPlural', { count, dates: dateLabel })
                    : t('description', { room: roomName, dates: dateLabel })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDismissed(true)}
                aria-label={t('dismiss')}
                className="ml-auto shrink-0 rounded-lg p-1.5 text-amber-700 transition-colors hover:bg-amber-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Two exits from the half-finished-booking state, each with a
                plain-language caption saying when to use it. */}
            <div className="flex flex-col gap-2.5 sm:flex-row">
              <div className="flex-1">
                <button
                  type="button"
                  onClick={handleResume}
                  className="w-full rounded-lg bg-[#801515] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#6a1111]"
                >
                  {t('resume')}
                </button>
                <p className="mt-1 px-0.5 text-xs leading-snug text-amber-800/80">
                  {multi ? t('resumeHintPlural') : t('resumeHint')}
                </p>
              </div>
              <div className="flex-1">
                <button
                  type="button"
                  onClick={handleRelease}
                  disabled={status === 'releasing'}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-amber-400 bg-white/60 px-3 py-2 text-sm font-medium text-amber-900 transition-colors hover:bg-white disabled:opacity-60"
                >
                  {status === 'releasing' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {status === 'releasing' ? t('releasing') : multi ? t('releasePlural') : t('release')}
                </button>
                <p className="mt-1 px-0.5 text-xs leading-snug text-amber-800/80">
                  {multi ? t('releaseHintPlural') : t('releaseHint')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
